import React from "react";
import { api, mediaUrl } from "@/lib/admin/api";
import { PageHead } from "@/pages/admin/Layout";
import { TextInput, TextArea, SelectInput, toast } from "@/components/admin/ui";
import { useLiveSiteBaseUrl, liveMenuUrl } from "@/lib/admin/liveSite";
import { SmartMedia, isVideoUrl as smIsVideo } from "@/components/admin/SmartMedia";

/* ====================================================================
   2High · Owner Cabinet — Products Manager (Feb 2026 redesign)
   --------------------------------------------------------------------
   Layout:
     LEFT  · product list with 60px thumbnails, bold media badges,
              category pill, stock pip, ★ featured, toggle/dup+open/edit
     RIGHT · slide-out edit panel (sticky, 620px)
              §A Basic info  ·  §B Photos (cover badge clear)
              §C Videos      ·  §D Live preview (real card mock)
              footer: Cancel · Save & Next · Save Product
              after save: also shows "View on Live Site"

   Backend uses 8 locked categories.
   ==================================================================== */

/* Locked 8 category IDs — labels are pulled live from /api/admin/categories
   so renames in the Categories page propagate to this dropdown automatically.
   Fallback labels used if the categories endpoint hasn't loaded yet. */
const FALLBACK_CATEGORIES = [
  { id: "wholesale",    label: "Flower Wholesale" },
  { id: "breakdown",    label: "Flower Breakdowns" },
  { id: "edibles",      label: "Edibles" },
  { id: "dispos",       label: "Dispos & Carts" },
  { id: "concentrates", label: "Concentrates" },
  { id: "infused",      label: "Infused" },
  { id: "moonrocks",    label: "Moonrocks" },
  { id: "flower",       label: "Specials / Retail Drops" },
];

const AVAIL = ["In stock", "Limited", "Sold out", "Coming soon"];

const BLANK = {
  name: "",
  category: "wholesale",
  section_label: "",
  size: "",
  price: "",
  compare_at_price: "",
  on_sale: false,
  notes: "",
  description: "",
  availability: "In stock",
  badge: "",
  tags: [],
  tiers: [],
  flavor_tags: [],
  effect_tags: [],
  best_for: [],
  sort: 0,
  img: "",
  img_set: {},
  videos: [],
  published: true,
  show_on_homepage: false,
};

function uid() { return Math.random().toString(36).slice(2, 10); }

function isVideoUrl(url) {
  if (!url) return false;
  const u = String(url).toLowerCase();
  return /\.(mp4|webm|mov|m4v|ogg|ogv)(\?|$)/.test(u) || /\/api\/media\/original\//.test(u);
}

/* Normalise a product DTO from the API into the editor's shape. */
function loadProduct(p) {
  const seen = new Set();
  const photos = [];
  const push = (url, extras = {}) => {
    if (!url) return;
    if (seen.has(url)) return;
    seen.add(url);
    photos.push({ id: uid(), url, set: extras.set || {}, isCover: extras.isCover || false });
  };
  // Primary cover img
  push(p.img, { set: p.img_set || {}, isCover: true });
  // Secondary img2 (legacy field — only included if not already covered)
  push(p.img2);
  // Extra photos[]
  if (Array.isArray(p.photos)) {
    p.photos.forEach((ph) => {
      if (typeof ph === "string") push(ph);
      else if (ph && ph.url) push(ph.url, { set: ph.set || {} });
    });
  }

  let videos = [];
  if (Array.isArray(p.videos) && p.videos.length) {
    videos = p.videos.map((v) => ({
      id: v.id || uid(),
      url: v.url || v.web_url || v.original_url || "",
      poster_url: v.poster_url || "",
      filename: v.filename || "",
      duration: v.duration || "",
      size_mb: v.size_mb || null,
    }));
  } else if ((p.video || "").trim()) {
    videos = [{ id: uid(), url: p.video, poster_url: p.video_poster || "", filename: "" }];
  }

  return {
    ...BLANK,
    ...p,
    photos,
    videos,
    badge: p.badge || "",
    tiers: Array.isArray(p.tiers) ? p.tiers.map((t) => ({
      key: t.key || "",
      label: t.label || "",
      price: Number(t.price) || 0,
      in_stock: t.in_stock !== false,
      note: t.note || "",
    })) : [],
    flavor_tags: Array.isArray(p.flavor_tags) ? p.flavor_tags : [],
    effect_tags: Array.isArray(p.effect_tags) ? p.effect_tags : [],
    best_for:    Array.isArray(p.best_for) ? p.best_for : [],
    show_on_homepage: !!p.show_on_homepage,
    published: p.published !== false,
  };
}

/* Convert editor state -> API payload. catLabel is a {id: label} map so the
   section_label fallback uses the latest live label from /api/admin/categories. */
function exportProduct(form, catLabel = {}) {
  const photos = form.photos || [];
  const cover = photos.find((p) => p.isCover) || photos[0] || null;
  const rest = photos.filter((p) => p !== cover);
  return {
    name: form.name,
    category: form.category,
    section_label: form.section_label || catLabel[form.category] || "",
    size: form.size,
    price: form.price || "",
    compare_at_price: form.compare_at_price || "",
    on_sale: !!form.on_sale,
    notes: form.notes,
    description: form.description,
    availability: form.availability,
    badge: form.badge || "",
    tags: form.tags || [],
    sort: Number(form.sort) || 0,
    img: cover ? cover.url : "",
    img_set: cover ? (cover.set || {}) : {},
    img2: "",  // deprecated — keep blank so loadProduct() doesn't double-count
    photos: rest.map((p) => ({ url: p.url, set: p.set || {} })),
    videos: (form.videos || []).map((v) => ({
      id: v.id, url: v.url, poster_url: v.poster_url || "",
      filename: v.filename || "", duration: v.duration || "", size_mb: v.size_mb || null,
    })),
    tiers: (form.tiers || []).map((t) => ({
      key: t.key || "",
      label: t.label || t.key || "",
      price: Number(t.price) || 0,
      in_stock: t.in_stock !== false,
      note: t.note || "",
    })),
    flavor_tags: (form.flavor_tags || []).filter(Boolean),
    effect_tags: (form.effect_tags || []).filter(Boolean),
    best_for:    (form.best_for || []).filter(Boolean),
    published: !!form.published,
    show_on_homepage: !!form.show_on_homepage,
  };
}

/* ============================== PAGE ============================== */
export default function Products() {
  const [products, setProducts] = React.useState([]);
  const [categories, setCategories] = React.useState(FALLBACK_CATEGORIES);
  const catLabel = React.useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c.label])),
    [categories]
  );
  const [search, setSearch] = React.useState("");
  const [catFilter, setCatFilter] = React.useState("all");
  const [stockFilter, setStockFilter] = React.useState("all");
  const [showHidden, setShowHidden] = React.useState(false);

  // Editor state — null = closed, "new" = creating, id = editing existing
  const [editingId, setEditingId] = React.useState(null);
  const [form, setForm] = React.useState(BLANK);
  const [saving, setSaving] = React.useState(false);
  const [lastSavedId, setLastSavedId] = React.useState(null);

  const reload = React.useCallback(async () => {
    const [list, cats] = await Promise.all([
      api.listProducts(false),
      api.getCategories().catch(() => []),
    ]);
    setProducts(list);
    if (Array.isArray(cats) && cats.length) {
      setCategories(
        [...cats].sort((a, b) => (a.sort || 0) - (b.sort || 0))
      );
    }
  }, []);

  React.useEffect(() => { reload(); }, [reload]);
  React.useEffect(() => {
    const fn = () => reload();
    window.addEventListener("admin:data:changed", fn);
    return () => window.removeEventListener("admin:data:changed", fn);
  }, [reload]);

  /* ---- list filter ---- */
  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return products
      .filter((p) => (showHidden ? true : p.published !== false))
      .filter((p) => catFilter === "all" || p.category === catFilter)
      .filter((p) => {
        if (stockFilter === "all") return true;
        return (p.availability || "").toLowerCase().includes(stockFilter);
      })
      .filter((p) => {
        if (!q) return true;
        return [p.name, p.notes, p.size, p.description, (p.tags || []).join(" ")]
          .join(" ").toLowerCase().includes(q);
      })
      .sort((a, b) => (a.sort || 0) - (b.sort || 0) || (a.created_at || "").localeCompare(b.created_at || ""));
  }, [products, search, catFilter, stockFilter, showHidden]);

  /* ---- reorder (swap sort with neighbour in filtered list) ---- */
  async function moveProduct(p, dir) {
    const idx = filtered.findIndex((x) => x.id === p.id);
    const target = filtered[idx + dir];
    if (!target) return;
    const a = p.sort || 0;
    const b = target.sort || 0;
    // If both 0, assign positional sorts so swap works
    const aSort = a || (idx + 1);
    const bSort = b || (idx + 1 + dir);
    try {
      // Optimistic update
      setProducts((cur) => cur.map((x) =>
        x.id === p.id ? { ...x, sort: bSort } :
        x.id === target.id ? { ...x, sort: aSort } : x
      ));
      await Promise.all([
        api.updateProduct(p.id, { sort: bSort }),
        api.updateProduct(target.id, { sort: aSort }),
      ]);
      reload();
    } catch (e) {
      toast("Reorder failed", "error");
      reload();
    }
  }

  /* ---- editor open / close ---- */
  function openNew() {
    setEditingId("new");
    setForm({ ...BLANK, sort: products.length + 1 });
    setLastSavedId(null);
  }
  function openEdit(p) {
    setEditingId(p.id);
    setForm(loadProduct(p));
    setLastSavedId(null);
  }
  function closeEditor() {
    setEditingId(null);
    setForm(BLANK);
    setLastSavedId(null);
  }

  /* ---- duplicate-last (top toolbar) ---- */
  async function duplicateLast() {
    const last = [...products].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];
    if (!last) return toast("No products yet", "warn");
    try {
      const dup = await api.duplicateProduct(last.id);
      toast("Duplicated last product", "ok");
      await reload();
      openEdit(dup);
    } catch (e) {
      toast(e.response?.data?.detail || "Duplicate failed", "error");
    }
  }

  /* ---- duplicate + open (per-row) ---- */
  async function duplicateAndOpen(p) {
    try {
      const dup = await api.duplicateProduct(p.id);
      toast("Duplicated · editing copy", "ok");
      await reload();
      openEdit(dup);
    } catch (e) {
      toast(e.response?.data?.detail || "Duplicate failed", "error");
    }
  }

  /* ---- toggle published ---- */
  async function togglePublished(p) {
    try {
      await api.updateProduct(p.id, { published: !(p.published !== false) });
      toast(p.published === false ? "Now visible on site" : "Hidden from site", "ok");
      reload();
    } catch (e) { toast("Update failed", "error"); }
  }

  /* ---- toggle featured on homepage ---- */
  async function toggleFeatured(p) {
    const next = !p.show_on_homepage;
    try {
      await api.updateProduct(p.id, { show_on_homepage: next });
      toast(next ? "Featured on homepage" : "Removed from homepage", "ok");
      reload();
    } catch (e) { toast("Update failed", "error"); }
  }

  async function deleteProduct(p) {
    if (!window.confirm(`Delete "${p.name}" permanently?\n\nThis removes it from the public site immediately.`)) return;
    try {
      await api.deleteProduct(p.id);
      toast("Product deleted", "ok");
      if (editingId === p.id) { setEditingId(null); setForm(BLANK); }
      reload();
    } catch (e) { toast("Delete failed", "error"); }
  }

  /* ---- save ---- */
  async function save({ thenNext = false } = {}) {
    if (!form.name?.trim()) return toast("Product name required", "error");
    setSaving(true);
    try {
      const payload = exportProduct(form, catLabel);
      let res;
      if (editingId === "new") res = await api.createProduct(payload);
      else res = await api.updateProduct(editingId, payload);
      setLastSavedId(res.id);
      toast(editingId === "new" ? "Product created" : "Saved", "ok");
      await reload();
      if (thenNext) {
        // Open a fresh blank for fast multi-entry
        setEditingId("new");
        setForm({ ...BLANK, sort: (products.length || 0) + 2, category: form.category });
        setLastSavedId(null);
      } else if (editingId === "new") {
        // Stay on the just-created product so user can keep editing media
        setEditingId(res.id);
        setForm(loadProduct(res));
      }
    } catch (e) {
      toast(e.response?.data?.detail || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  }

  /* ---- bulk hide ---- */
  async function bulkHide() {
    const visible = filtered.filter((p) => p.published !== false);
    if (!visible.length) return;
    if (!window.confirm(`Hide ${visible.length} visible products from the site?`)) return;
    await Promise.all(visible.map((p) => api.updateProduct(p.id, { published: false })));
    toast(`${visible.length} hidden`, "ok");
    reload();
  }

  /* ============================== RENDER ============================== */
  return (
    <div data-testid="products-page" className="-mx-4 lg:-mx-8 -my-4 lg:-my-8 min-h-screen px-4 lg:px-8 py-4 lg:py-8">
      <PageHead
        eye={`Catalog · ${products.length} products · 8 categories`}
        title="Products"
        sub={products[0]?.updated_at ? `Last edit · ${timeAgo(latestUpdate(products))} · ${latestUpdateName(products)}` : "Manage your live menu"}
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid="btn-duplicate-last"
              onClick={duplicateLast}
              className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-lg border border-[#c9a86b]/25 bg-white/[.03] hover:border-[#c9a86b] hover:bg-[#c9a86b]/[.06] text-[12px] tracking-[.18em] uppercase text-[#e8e4d8]/85 transition"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M16 4h4v4M20 4l-7 7M8 20H4v-4M4 20l7-7"/></svg>
              Duplicate Last
            </button>
            <button
              data-testid="btn-bulk-hide"
              onClick={bulkHide}
              className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-lg border border-[#c9a86b]/25 bg-white/[.03] hover:border-[#c9a86b] hover:bg-[#c9a86b]/[.06] text-[12px] tracking-[.18em] uppercase text-[#e8e4d8]/85 transition"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M3 3l18 18M10.6 10.6a2 2 0 0 0 2.8 2.8M9.9 4.2A10.5 10.5 0 0 1 12 4c4.5 0 8.4 2.7 10 7-1 2.7-3 4.8-5.5 6M6.5 6.5C4.4 7.9 2.7 10 2 12c1.6 4.3 5.5 7 10 7 1.6 0 3.1-.3 4.4-1"/></svg>
              Bulk Hide
            </button>
            <button
              data-testid="btn-add-new"
              onClick={openNew}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-[12px] tracking-[.18em] uppercase font-bold transition"
              style={{ background: "linear-gradient(180deg,#d4b675,#a07c39)", color: "#1a1407" }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
              Add New Product
            </button>
          </div>
        }
      />

      {/* Homepage featured-tile hint — visible whenever the operator has < 4 starred */}
      {(() => {
        const featCount = products.filter((p) => p.show_on_homepage === true && p.published !== false).length;
        if (featCount >= 4 || products.length === 0) return null;
        return (
          <div
            data-testid="featured-hint"
            className="mb-4 flex items-center gap-3 px-4 py-3 rounded-xl border border-[#c9a86b]/35 bg-[#231d10]/60 text-[12px] text-[#e8e4d8]/85"
          >
            <span className="text-[18px] text-[#c9a86b]">★</span>
            <div>
              <b className="text-[#c9a86b]">Homepage "This Week's Drops"</b> shows up to 4 products.
              You've starred <b>{featCount}</b> — click the <span className="text-[#c9a86b]">☆</span> column to feature more.
              Un-starred slots are filled with your newest products automatically.
            </div>
          </div>
        );
      })()}

      {/* Toolbar */}
      <div className="mb-5 grid grid-cols-1 md:grid-cols-[1fr_220px_180px_auto] gap-2 p-2.5 rounded-xl bg-[#13150f] border border-[#22261b]">
        <div className="relative">
          <svg className="absolute left-3 top-3 opacity-60" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 21l-4.3-4.3M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z"/></svg>
          <input
            data-testid="products-search"
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, batch, notes, tag…"
            className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-[#0e110a] border border-[#22261b] text-[13px] focus:outline-none focus:border-[#c9a86b]"
          />
        </div>
        <select
          data-testid="filter-category"
          value={catFilter} onChange={(e) => setCatFilter(e.target.value)}
          className="px-3 py-2.5 rounded-lg bg-[#0e110a] border border-[#22261b] text-[13px] focus:outline-none focus:border-[#c9a86b]"
        >
          <option value="all">All Categories ({products.length})</option>
          {categories.map((c) => {
            const n = products.filter((p) => p.category === c.id).length;
            return <option key={c.id} value={c.id}>{c.label} ({n})</option>;
          })}
        </select>
        <select
          data-testid="filter-stock"
          value={stockFilter} onChange={(e) => setStockFilter(e.target.value)}
          className="px-3 py-2.5 rounded-lg bg-[#0e110a] border border-[#22261b] text-[13px] focus:outline-none focus:border-[#c9a86b]"
        >
          <option value="all">All Stock</option>
          <option value="in stock">In stock</option>
          <option value="limited">Limited</option>
          <option value="sold">Sold out</option>
          <option value="coming">Coming soon</option>
        </select>
        <button
          data-testid="filter-show-hidden"
          onClick={() => setShowHidden((v) => !v)}
          className={`px-3 py-2.5 rounded-lg text-[11px] tracking-[.2em] uppercase border transition ${
            showHidden ? "bg-[#c9a86b] text-[#1a1407] border-[#c9a86b]" : "bg-[#0e110a] text-[#e8e4d8]/65 border-[#22261b] hover:border-[#c9a86b]"
          }`}
        >{showHidden ? "Showing Hidden" : "Hidden Off"}</button>
      </div>

      {/* Layout: list + edit panel */}
      <div className={`grid gap-5 ${editingId ? "grid-cols-1 xl:grid-cols-[1fr_640px]" : "grid-cols-1"}`}>

        {/* ============== LIST ============== */}
        <section data-testid="products-list" className="rounded-2xl border border-[#22261b] bg-[#13150f] overflow-hidden">
          <div className="grid grid-cols-[68px_1.4fr_0.8fr_0.7fr_0.4fr_0.7fr_220px] gap-3 items-center px-4 py-3 text-[10px] tracking-[.22em] uppercase text-[#6e6f5f] bg-[#10120c] border-b border-[#22261b]">
            <div></div>
            <div>Name</div>
            <div>Category</div>
            <div>Stock</div>
            <div>★</div>
            <div>Media</div>
            <div className="text-right">Actions</div>
          </div>

          {filtered.length === 0 ? (
            <div className="p-12 text-center text-[#6e6f5f] text-[13px]">
              {products.length === 0
                ? <>No products yet. Click <span className="text-[#c9a86b]">Add New Product</span> to start.</>
                : "No products match these filters."}
            </div>
          ) : filtered.map((p, idx) => (
            <ProductRow
              key={p.id}
              p={p}
              isFirst={idx === 0}
              isLast={idx === filtered.length - 1}
              selected={editingId === p.id}
              onEdit={() => openEdit(p)}
              onDuplicate={() => duplicateAndOpen(p)}
              onTogglePublished={() => togglePublished(p)}
              onToggleFeatured={() => toggleFeatured(p)}
              onMoveUp={() => moveProduct(p, -1)}
              onMoveDown={() => moveProduct(p, 1)}
              onDelete={() => deleteProduct(p)}
            />
          ))}
        </section>

        {/* ============== EDIT PANEL ============== */}
        {editingId && (
          <EditPanel
            key={editingId}
            isNew={editingId === "new"}
            form={form}
            setForm={setForm}
            saving={saving}
            lastSavedId={lastSavedId}
            categories={categories}
            catLabel={catLabel}
            onClose={closeEditor}
            onSave={() => save({ thenNext: false })}
            onSaveAndNext={() => save({ thenNext: true })}
          />
        )}
      </div>
    </div>
  );
}

/* ============================== ROW ============================== */
function ProductRow({ p, selected, onEdit, onDuplicate, onTogglePublished, onToggleFeatured, onMoveUp, onMoveDown, onDelete, isFirst, isLast }) {
  // Admin row only needs a tiny 90x90 thumbnail — prefer the small WebP (40-80 KB)
  // over md (200-300 KB) or lg (400-600 KB) to keep the page snappy with many products.
  const cover = (p.img_set?.sm) || p.img || (p.img_set?.md) || (p.img_set?.lg) || "";
  const photoCount = 1 + (p.photos?.length || 0) + (p.img2 && p.img2 !== p.img ? 1 : 0) - (cover ? 0 : 1);
  const videoCount = (p.videos?.length || 0) || (p.video ? 1 : 0);
  const stock = stockOf(p.availability);
  const featured = !!p.show_on_homepage;
  const visible = p.published !== false;

  return (
    <div
      data-testid={`product-row-${p.id}`}
      onClick={onEdit}
      className={`grid grid-cols-[68px_1.4fr_0.8fr_0.7fr_0.4fr_0.7fr_220px] gap-3 items-center px-4 py-3 border-b border-[#22261b] cursor-pointer transition ${
        selected ? "bg-[#181c12] shadow-[inset_3px_0_0_#c9a86b]" : "hover:bg-[#10130c]"
      } ${!visible ? "opacity-50" : ""}`}
    >
      <div
        className="w-14 h-14 rounded-lg border border-[#2c3122] bg-[#0e110a] flex-shrink-0 relative overflow-hidden"
      >
        {cover ? (
          <ThumbImage src={mediaUrl(cover)} alt={p.name || "product"} />
        ) : (
          <div className="w-full h-full grid place-items-center text-[#6e6f5f] text-[10px]">no img</div>
        )}
      </div>
      <div className="min-w-0">
        <div className="text-[14px] font-semibold truncate">{p.name || <span className="text-[#6e6f5f]">(unnamed)</span>}</div>
        <div className="text-[10.5px] tracking-[.06em] uppercase text-[#6e6f5f] mt-0.5 font-mono truncate">
          {[p.size, p.notes].filter(Boolean).join(" · ") || "—"}
        </div>
      </div>
      <div>
        <span className="inline-flex items-center px-2 py-1 rounded-md text-[10.5px] bg-[#1a1e13] text-[#bbc09e] border border-[#2c3122] font-mono tracking-[.04em]">
          {p.category}
        </span>
      </div>
      <div>
        <span className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold ${stock.cls}`}>
          <span className="w-[7px] h-[7px] rounded-full" style={{ background: stock.color, boxShadow: `0 0 8px ${stock.color}80` }} />
          {p.availability || "—"}
        </span>
      </div>
      <button
        type="button"
        data-testid={`row-feature-${p.id}`}
        onClick={(e) => { e.stopPropagation(); onToggleFeatured(); }}
        title={featured ? "Featured on homepage · click to remove" : "Click to feature on the homepage drops grid"}
        className="text-[18px] leading-none hover:scale-110 transition-transform"
        style={{ color: featured ? "#c9a86b" : "#3a3f2e" }}
      >{featured ? "★" : "☆"}</button>
      <div className="flex items-center gap-2">
        <span data-testid={`media-photos-${p.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#0e110a] border border-[#2c3122] text-[11.5px] font-bold text-[#e8e4d8]">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="9" cy="11" r="2"/><path d="M21 17l-5-5-7 7"/></svg>
          {photoCount}
        </span>
        <span data-testid={`media-videos-${p.id}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11.5px] font-bold ${
          videoCount ? "bg-[#231d10] border border-[#c9a86b]/40 text-[#c9a86b]" : "bg-[#0e110a] border border-[#2c3122] text-[#6e6f5f]"
        }`}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polygon points="6 4 20 12 6 20 6 4"/></svg>
          {videoCount}
        </span>
      </div>
      <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-col">
          <button
            data-testid={`row-up-${p.id}`}
            onClick={onMoveUp}
            disabled={isFirst}
            title="Move up"
            className="px-1.5 text-[10px] leading-none text-[#a9aa9a] hover:text-[#c9a86b] disabled:opacity-20"
          >▲</button>
          <button
            data-testid={`row-down-${p.id}`}
            onClick={onMoveDown}
            disabled={isLast}
            title="Move down"
            className="px-1.5 text-[10px] leading-none text-[#a9aa9a] hover:text-[#c9a86b] disabled:opacity-20"
          >▼</button>
        </div>
        <button
          data-testid={`row-toggle-${p.id}`}
          onClick={onTogglePublished}
          title={visible ? "Hide from site" : "Show on site"}
          className={`relative w-9 h-5 rounded-full border transition ${visible ? "bg-[#2d3a23] border-[#5fa15a]" : "bg-[#1f2218] border-[#2c3122]"}`}
        >
          <span className={`absolute top-[1px] w-3.5 h-3.5 rounded-full transition-all ${visible ? "left-[18px] bg-[#5fa15a]" : "left-[2px] bg-[#6e6f5f]"}`} />
        </button>
        <button
          data-testid={`row-duplicate-${p.id}`}
          onClick={onDuplicate}
          title="Duplicate + open"
          className="w-8 h-8 rounded-md border border-[#2c3122] grid place-items-center text-[#a9aa9a] hover:border-[#c9a86b] hover:text-[#c9a86b] transition"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="11" height="11" rx="1.5"/><path d="M5 15V5a1 1 0 0 1 1-1h10"/></svg>
        </button>
        <button
          data-testid={`row-edit-${p.id}`}
          onClick={onEdit}
          title="Edit"
          className={`w-8 h-8 rounded-md border grid place-items-center transition ${
            selected ? "border-[#c9a86b] text-[#c9a86b] bg-[#231d10]" : "border-[#2c3122] text-[#a9aa9a] hover:border-[#c9a86b] hover:text-[#c9a86b]"
          }`}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4v16h16v-7M18.5 2.5a2.1 2.1 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        </button>
        <button
          data-testid={`row-delete-${p.id}`}
          onClick={onDelete}
          title="Delete product"
          className="w-8 h-8 rounded-md border border-red-500/30 grid place-items-center text-red-400 hover:bg-red-500/15 hover:border-red-500/60 transition"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
        </button>
      </div>
    </div>
  );
}

/* ============================== EDIT PANEL ============================== */
function EditPanel({ isNew, form, setForm, saving, lastSavedId, categories, catLabel, onClose, onSave, onSaveAndNext }) {
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const liveBase = useLiveSiteBaseUrl();
  const liveSiteHref = liveMenuUrl(liveBase, form.category);

  return (
    <aside data-testid="edit-panel" className="rounded-2xl border border-[#22261b] bg-[#13150f] overflow-hidden flex flex-col xl:sticky xl:top-6 xl:max-h-[calc(100vh-3rem)]">
      {/* Header */}
      <div className="flex items-start justify-between px-5 py-4 border-b border-[#22261b] bg-[#10120c]">
        <div>
          <h2 className="text-[16px] font-bold leading-tight">{isNew ? "New Product" : `Edit · ${form.name || "(unnamed)"}`}</h2>
          <div className="text-[10.5px] tracking-[.08em] font-mono text-[#6e6f5f] mt-1">
            {isNew ? "Will be created on save" : `id · ${(form.id || "").slice(0, 8)} · ${form.updated_at ? "updated " + timeAgo(form.updated_at) : ""}`}
          </div>
        </div>
        <button
          data-testid="edit-close"
          onClick={onClose}
          className="w-8 h-8 rounded-md border border-[#2c3122] grid place-items-center text-[#a9aa9a] hover:border-[#c9a86b] hover:text-[#c9a86b]"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
        </button>
      </div>

      {/* Body */}
      <div className="px-5 py-5 overflow-y-auto flex-1 space-y-7">
        <SectionA form={form} set={set} categories={categories} catLabel={catLabel} />
        <SectionB form={form} setForm={setForm} />
        <SectionC form={form} setForm={setForm} />
        <SectionD form={form} />
      </div>

      {/* Footer */}
      <div className="px-5 py-4 border-t border-[#22261b] bg-[#10120c] flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] font-mono tracking-[.06em] text-[#6e6f5f]">
          {lastSavedId ? <span className="text-[#5fa15a]">● Saved</span> : "Unsaved changes are discarded on close"}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {lastSavedId && (
            <a
              data-testid="btn-view-on-site"
              href={liveSiteHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#c9a86b]/40 text-[#c9a86b] hover:bg-[#c9a86b]/[.08] text-[11px] tracking-[.2em] uppercase font-semibold"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M14 3h7v7M21 3l-9 9M19 14v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6"/></svg>
              View on Live Site
            </a>
          )}
          <button
            data-testid="btn-cancel"
            onClick={onClose}
            className="px-4 py-2.5 rounded-md border border-[#2c3122] text-[#a9aa9a] hover:border-[#c9a86b] text-[11px] tracking-[.2em] uppercase"
          >Cancel</button>
          <button
            data-testid="btn-save-and-next"
            onClick={onSaveAndNext}
            disabled={saving}
            className="px-4 py-2.5 rounded-md border border-[#c9a86b]/50 text-[#c9a86b] hover:bg-[#c9a86b]/[.08] disabled:opacity-50 text-[11px] tracking-[.2em] uppercase font-semibold"
          >Save &amp; Next</button>
          <button
            data-testid="btn-save"
            onClick={onSave}
            disabled={saving}
            className="px-5 py-2.5 rounded-md text-[12px] tracking-[.2em] uppercase font-bold disabled:opacity-50"
            style={{ background: "linear-gradient(180deg,#d4b675,#a07c39)", color: "#1a1407" }}
          >{saving ? "Saving…" : "Save Product"}</button>
        </div>
      </div>
    </aside>
  );
}

/* ============================== SECTION A — Basic Info ============================== */
function SectionA({ form, set, categories = FALLBACK_CATEGORIES, catLabel = {} }) {
  const onCategoryChange = (id) => set({ category: id, section_label: form.section_label || catLabel[id] || "" });
  return (
    <Section letter="A" title="Basic Info">
      <Field label="Product name">
        <TextInput data-testid="field-name" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Cherry Pie · Indoor" />
      </Field>
      <div className="grid grid-cols-[1.3fr_1fr_0.7fr] gap-2.5">
        <Field label="Category">
          <SelectInput
            data-testid="field-category"
            value={form.category}
            onChange={(e) => onCategoryChange(e.target.value)}
            options={categories.map((c) => ({ value: c.id, label: c.label }))}
          />
        </Field>
        <Field label="Size / Weight">
          <TextInput data-testid="field-size" value={form.size} onChange={(e) => set({ size: e.target.value })} placeholder="1 oz · 28g" />
        </Field>
        <Field label="Sort">
          <TextInput data-testid="field-sort" type="number" value={form.sort} onChange={(e) => set({ sort: e.target.value })} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        <Field label="Stock status">
          <SelectInput
            data-testid="field-availability"
            value={form.availability}
            onChange={(e) => set({ availability: e.target.value })}
            options={AVAIL.map((a) => ({ value: a, label: a }))}
          />
        </Field>
        <Field label="Price (display)">
          <TextInput data-testid="field-price" value={form.price || ""} onChange={(e) => set({ price: e.target.value })} placeholder="$1,250" />
        </Field>
      </div>

      {/* Sale / Discount pricing */}
      <div className="rounded-lg border border-[#c9a86b]/25 bg-[#1a1810]/40 p-3.5 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[10.5px] tracking-[.18em] uppercase text-[#c9a86b] font-mono">Sale / Discount</div>
            <div className="text-[10.5px] text-[#6e6f5f] mt-0.5">Turn on to show a strike-through "was" price next to the current price.</div>
          </div>
          <ToggleRow
            data-testid="toggle-on-sale"
            label={form.on_sale ? "On Sale" : "Off"}
            checked={!!form.on_sale}
            onChange={(v) => set({ on_sale: v })}
          />
        </div>
        {form.on_sale && (
          <div className="grid grid-cols-2 gap-2.5">
            <Field label="Was (original price)">
              <TextInput
                data-testid="field-compare-at"
                value={form.compare_at_price || ""}
                onChange={(e) => set({ compare_at_price: e.target.value })}
                placeholder="$1,350"
              />
            </Field>
            <Field label="Now (sale price)">
              <TextInput
                value={form.price || ""}
                onChange={(e) => set({ price: e.target.value })}
                placeholder="$1,250"
              />
            </Field>
          </div>
        )}
        {form.on_sale && form.compare_at_price && form.price && (
          <div className="text-[11.5px] text-[#a8d39d] flex items-center gap-2 pt-1">
            <span className="inline-block px-2 py-0.5 rounded-md text-[10px] tracking-[.16em] uppercase font-bold" style={{ background: "linear-gradient(180deg,#d4b675,#a07c39)", color: "#1a1407" }}>Sale</span>
            <s className="text-[#6e6f5f]">{form.compare_at_price}</s>
            <span className="font-semibold">{form.price}</span>
          </div>
        )}
      </div>

      {/* Mix & Match Tiers — premium structured pricing for the Drop Builder */}
      <TierEditor form={form} set={set} />

      {/* Trait tags — flavor, effect, best-for chips shown on the public modal */}
      <TagListEditor
        label="Flavor profile"
        hint="Comma-separate or hit Enter after each. Examples: Candy, Gas, Cream, Citrus, Earth."
        value={form.flavor_tags || []}
        onChange={(arr) => set({ flavor_tags: arr })}
        testid="field-flavor-tags"
      />
      <TagListEditor
        label="Effects"
        hint="How members feel. Examples: Heavy, Euphoric, Relaxing, Daytime, Couch-lock."
        value={form.effect_tags || []}
        onChange={(arr) => set({ effect_tags: arr })}
        testid="field-effect-tags"
      />
      <TagListEditor
        label="Best for"
        hint='Use cases / vibes. Examples: "Late night sessions", "Creative work", "Music", "Social rotation".'
        value={form.best_for || []}
        onChange={(arr) => set({ best_for: arr })}
        testid="field-best-for"
      />

      <Field label="Badge">
        <TextInput data-testid="field-badge" value={form.badge} onChange={(e) => set({ badge: e.target.value })} placeholder="MEMBERS DROP" />
      </Field>
      <Field label="Notes (short)">
        <TextInput data-testid="field-notes" value={form.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="BATCH 044 · 28.3% · Indica-lean" />
      </Field>
      <Field
        label="Description"
        hint="Press Enter for line breaks · blank line for paragraph break · start a line with • or ✔ for a bullet · use 'Label:' at the start of a line to highlight it gold (e.g. 'Strain Type:')."
      >
        <TextArea
          data-testid="field-description"
          value={form.description}
          rows={10}
          onChange={(e) => set({ description: e.target.value })}
          placeholder={`✔️ 2High Quality Control Certified ✔️

Experience Top Shelf indoor exotics. This rare cross delivers exceptional quality from nose to nug:

• Strain Type: Top Shelf Indica-Dominant Hybrid
• Aroma: Candy-forward profile — unmistakably loud nose
• Appearance: Vibrant colors, big dense buds
• Trichomes: Heavy crystal coverage — 10/10
• Grown: Indoor, hand-trimmed, 100% clean flower

Elite quality at an unbeatable price.`}
        />
      </Field>
      <Field label="Tags (comma-separated)">
        <TextInput
          data-testid="field-tags"
          value={(form.tags || []).join(", ")}
          onChange={(e) => set({ tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
          placeholder="indoor, indica-lean, exotic"
        />
      </Field>
      <div className="flex items-center gap-5 pt-1">
        <ToggleRow data-testid="toggle-active" label="Active (visible on site)" checked={form.published} onChange={(v) => set({ published: v })} />
        <ToggleRow data-testid="toggle-featured" label="Featured on homepage" checked={form.show_on_homepage} onChange={(v) => set({ show_on_homepage: v })} />
      </div>
    </Section>
  );
}

/* ============================== SECTION B — Photos ============================== */
function SectionB({ form, setForm }) {
  const photos = form.photos || [];
  const inputRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);

  async function handleFiles(files) {
    if (!files?.length) return;
    setBusy(true);
    for (const f of files) {
      if (!f.type.startsWith("image/")) { toast(`${f.name}: not an image`, "warn"); continue; }
      if (f.size > 25 * 1024 * 1024) { toast(`${f.name}: too large (max 25 MB)`, "error"); continue; }
      try {
        const meta = await api.uploadMedia(f);
        const url = meta.web_url || meta.url || meta.url_original;
        setForm((cur) => {
          const list = cur.photos || [];
          const newPhoto = { id: uid(), url, set: meta.img_set || meta.set || {}, isCover: list.length === 0 };
          return { ...cur, photos: [...list, newPhoto] };
        });
      } catch (e) {
        const msg = e.response?.data?.detail
          || (e.code === "ECONNABORTED" ? "Upload timed out — try again or shrink the file." : null)
          || (e.message?.includes("Network") ? `Network error uploading "${f.name}" — check connection.` : null)
          || `Upload failed for "${f.name}". ${e.message || ""}`;
        toast(msg, "error");
      }
    }
    setBusy(false);
  }
  function setCover(id) {
    setForm((cur) => ({
      ...cur,
      photos: (cur.photos || []).map((p) => ({ ...p, isCover: p.id === id })),
    }));
  }
  function removePhoto(id) {
    setForm((cur) => {
      const left = (cur.photos || []).filter((p) => p.id !== id);
      // ensure cover exists
      if (left.length && !left.some((p) => p.isCover)) left[0].isCover = true;
      return { ...cur, photos: left };
    });
  }
  function move(id, dir) {
    setForm((cur) => {
      const list = [...(cur.photos || [])];
      const i = list.findIndex((p) => p.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= list.length) return cur;
      [list[i], list[j]] = [list[j], list[i]];
      return { ...cur, photos: list };
    });
  }

  return (
    <Section letter="B" title={`Photos · ${photos.length} / 8`}>
      <div
        data-testid="photo-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        className="rounded-xl border-[1.5px] border-dashed border-[#2c3122] hover:border-[#c9a86b] cursor-pointer text-center py-5 px-4 text-[12.5px] text-[#6e6f5f] hover:text-[#c9a86b] transition"
        style={{ background: "repeating-linear-gradient(135deg,#0e110a,#0e110a 8px,#10130c 8px,#10130c 16px)" }}
      >
        <strong className="text-[#e8e4d8]">Drop photos here</strong> or click to browse · WebP / JPG / PNG · max 25 MB each
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />

      <div className="grid grid-cols-4 gap-2.5 mt-3">
        {photos.map((p, i) => (
          <div
            key={p.id}
            data-testid={`photo-tile-${i}`}
            className={`relative aspect-square rounded-xl overflow-hidden border bg-[#0a0c08] group ${p.isCover ? "border-[#c9a86b] shadow-[0_0_0_2px_#c9a86b40]" : "border-[#2c3122]"}`}
          >
            <ThumbImage src={mediaUrl(p.url)} alt={`photo ${i + 1}`} />
            {p.isCover ? (
              <div className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded bg-[#c9a86b] text-[#1a1407] text-[9.5px] tracking-[.18em] uppercase font-bold flex items-center gap-1 z-10">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15 9 22 9 17 14 19 21 12 17 5 21 7 14 2 9 9 9"/></svg>
                Cover
              </div>
            ) : (
              <button
                data-testid={`photo-set-cover-${i}`}
                onClick={() => setCover(p.id)}
                title="Set as cover"
                className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded bg-black/70 text-white text-[9.5px] tracking-[.18em] uppercase opacity-0 group-hover:opacity-100 hover:bg-[#c9a86b] hover:text-[#1a1407] transition z-10"
              >Make cover</button>
            )}
            <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition z-10">
              <button data-testid={`photo-up-${i}`} onClick={() => move(p.id, -1)} className="w-6 h-6 rounded bg-black/70 grid place-items-center text-white text-[12px]">↑</button>
              <button data-testid={`photo-down-${i}`} onClick={() => move(p.id, 1)} className="w-6 h-6 rounded bg-black/70 grid place-items-center text-white text-[12px]">↓</button>
              <button data-testid={`photo-delete-${i}`} onClick={() => removePhoto(p.id)} className="w-6 h-6 rounded bg-black/70 grid place-items-center text-white text-[12px]">×</button>
            </div>
            <div className="absolute bottom-0 left-0 right-0 px-2 py-1 text-[9.5px] tracking-[.18em] text-white/80 font-mono bg-gradient-to-t from-black/80 to-transparent z-10">
              {p.isCover ? "first card image" : `position ${i + 1}`}
            </div>
          </div>
        ))}
        <button
          data-testid="photo-add-tile"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="aspect-square rounded-xl border-2 border-dashed border-[#2c3122] bg-[#0e110a] grid place-items-center text-[#6e6f5f] hover:text-[#c9a86b] hover:border-[#c9a86b] transition"
        >
          <div className="text-center">
            <div className="text-[26px] leading-none">+</div>
            <div className="text-[10px] mt-1 tracking-[.18em] uppercase">{busy ? "Uploading…" : "Add Photo"}</div>
          </div>
        </button>
      </div>
    </Section>
  );
}

/* ============================== SECTION C — Videos ============================== */
function SectionC({ form, setForm }) {
  const videos = form.videos || [];
  const inputRef = React.useRef(null);
  const [progress, setProgress] = React.useState({}); // {tempId: pct}

  async function handleFiles(files) {
    if (!files?.length) return;
    for (const f of files) {
      if (!f.type.startsWith("video/")) { toast(`${f.name}: not a video`, "warn"); continue; }
      if (f.size > 200 * 1024 * 1024) { toast(`${f.name}: too large (max 200 MB)`, "error"); continue; }
      const tempId = uid();
      setProgress((p) => ({ ...p, [tempId]: 0 }));
      try {
        const meta = await api.uploadMedia(f, (pct) => setProgress((p) => ({ ...p, [tempId]: pct })));
        const url = meta.web_url || meta.url || meta.url_original;
        setForm((cur) => ({
          ...cur,
          videos: [...(cur.videos || []), {
            id: uid(), url,
            poster_url: meta.poster_url || "",
            filename: f.name, duration: meta.duration || "",
            size_mb: Math.round((f.size / 1048576) * 10) / 10,
          }],
        }));
        setProgress((p) => { const n = { ...p }; delete n[tempId]; return n; });
      } catch (e) {
        setProgress((p) => { const n = { ...p }; delete n[tempId]; return n; });
        const msg = e.response?.data?.detail
          || (e.code === "ECONNABORTED" ? "Upload timed out — file too large or slow connection. Try again." : null)
          || (e.message?.includes("Network") ? `Network error uploading "${f.name}" — check connection and retry.` : null)
          || `Upload failed for "${f.name}". ${e.message || ""}`;
        toast(msg, "error");
      }
    }
  }
  function remove(id) { setForm((cur) => ({ ...cur, videos: (cur.videos || []).filter((v) => v.id !== id) })); }
  function move(id, dir) {
    setForm((cur) => {
      const list = [...(cur.videos || [])];
      const i = list.findIndex((v) => v.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= list.length) return cur;
      [list[i], list[j]] = [list[j], list[i]];
      return { ...cur, videos: list };
    });
  }

  return (
    <Section letter="C" title={`Videos · ${videos.length} / 4`}>
      <div
        data-testid="video-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        className="rounded-xl border-[1.5px] border-dashed border-[#2c3122] hover:border-[#c9a86b] cursor-pointer text-center py-5 px-4 text-[12.5px] text-[#6e6f5f] hover:text-[#c9a86b] transition"
        style={{ background: "repeating-linear-gradient(135deg,#0e110a,#0e110a 8px,#10130c 8px,#10130c 16px)" }}
      >
        <strong className="text-[#e8e4d8]">Drop videos here</strong> or click to browse · MP4 / MOV · max 200 MB · auto-compressed to 1080p ~6 MB
      </div>
      <input ref={inputRef} type="file" accept="video/*" multiple className="hidden"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />

      <div className="space-y-2 mt-3">
        {Object.entries(progress).map(([tid, pct]) => (
          <div key={tid} data-testid={`video-uploading-${tid}`} className="flex items-center gap-3 p-2.5 bg-[#0e110a] border border-[#2c3122] rounded-lg">
            <div className="w-16 h-12 rounded-md bg-[#1a1d12] grid place-items-center text-[#c9a86b] text-[18px] flex-shrink-0">⟳</div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold truncate">Uploading…</div>
              <div className="text-[10.5px] font-mono text-[#c9a86b]">COMPRESSING · {pct}%</div>
              <div className="h-[5px] bg-[#191c14] rounded-full overflow-hidden mt-1.5">
                <div className="h-full transition-all" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#c9a86b,#e3c98c)" }} />
              </div>
            </div>
          </div>
        ))}
        {videos.map((v, i) => (
          <div key={v.id} data-testid={`video-row-${i}`} className="flex items-center gap-3 p-2.5 bg-[#0e110a] border border-[#2c3122] rounded-lg">
            <div className="w-16 h-12 rounded-md border border-[#2c3122] bg-cover bg-center flex-shrink-0"
              style={v.poster_url ? { backgroundImage: `url(${mediaUrl(v.poster_url)})` } : { background: "#1a1d12" }} />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold truncate">{v.filename || "video"}</div>
              <div className="text-[10.5px] font-mono text-[#6e6f5f] mt-0.5">
                <span className="text-[#5fa15a]">UPLOADED</span>
                {v.size_mb ? ` · ${v.size_mb} MB` : ""}{v.duration ? ` · ${v.duration}` : ""}{v.poster_url ? " · poster ready" : ""}
              </div>
            </div>
            <button data-testid={`video-up-${i}`} onClick={() => move(v.id, -1)} className="w-7 h-7 rounded-md border border-[#2c3122] grid place-items-center text-[#a9aa9a] hover:border-[#c9a86b] hover:text-[#c9a86b]">↑</button>
            <button data-testid={`video-down-${i}`} onClick={() => move(v.id, 1)} className="w-7 h-7 rounded-md border border-[#2c3122] grid place-items-center text-[#a9aa9a] hover:border-[#c9a86b] hover:text-[#c9a86b]">↓</button>
            <button data-testid={`video-delete-${i}`} onClick={() => remove(v.id)} className="w-7 h-7 rounded-md border border-[#2c3122] grid place-items-center text-[#a9aa9a] hover:border-red-400 hover:text-red-400">×</button>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ============================== SECTION D — Live Preview ============================== */
function SectionD({ form }) {
  const cover = (form.photos || []).find((p) => p.isCover) || (form.photos || [])[0];
  const firstVideo = (form.videos || [])[0];
  const stock = stockOf(form.availability);
  // Premium card: use the cover photo (or video poster) in a large rectangular
  // media area at the top of the card. NEVER circle-crop.
  const mediaSrc = cover ? mediaUrl(cover.url) : "";
  const videoSrc = firstVideo ? mediaUrl(firstVideo.url || "") : "";

  return (
    <Section letter="D" title="Live Preview">
      <div className="rounded-2xl border border-[#2c3122] bg-[#0a0c08] overflow-hidden">
        <div className="px-3.5 py-2.5 border-b border-[#22261b] bg-[#10120c] flex items-center justify-between text-[10.5px] tracking-[.18em] uppercase text-[#6e6f5f]">
          <span>How it appears on 2high.com</span>
          <span className="text-[#5fa15a]">● Live render</span>
        </div>
        <div className="p-4">
          <SmartMedia
            src={videoSrc || mediaSrc}
            poster={mediaSrc}
            alt={form.name || "product"}
            aspect="4/3"
            badge={form.badge || null}
            videoBadge={!!firstVideo}
            showPlay={!!firstVideo}
          >
            {!cover && (
              <div className="absolute inset-0 grid place-items-center text-[#6e6f5f] text-[12px]">No cover photo yet</div>
            )}
          </SmartMedia>
          {form.section_label || form.badge ? (
            <div className="mt-3 text-[10.5px] tracking-[.18em] font-mono uppercase text-[#c9a86b]">
              {form.badge ? `${form.badge}${form.section_label ? " · " + form.section_label : ""}` : form.section_label}
            </div>
          ) : null}
          <div className="text-[18px] font-bold tracking-tight mt-1.5 leading-tight">
            {form.name || <span className="text-[#6e6f5f] font-normal">Untitled product</span>}
          </div>
          {form.size && <div className="text-[11.5px] font-mono tracking-[.08em] uppercase text-[#6e6f5f] mt-1">{form.size}</div>}
          {form.description && <p className="text-[13px] text-[#a9aa9a] mt-2.5 leading-relaxed">{form.description}</p>}
          <div className="flex items-center justify-between mt-3">
            <span className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold ${stock.cls}`}>
              <span className="w-[7px] h-[7px] rounded-full" style={{ background: stock.color }} />
              {form.availability}
            </span>
            <span className="px-2 py-1 rounded-md bg-[#1a1e13] text-[#bbc09e] border border-[#2c3122] font-mono text-[10.5px] tracking-[.04em]">
              {form.category}
            </span>
          </div>
          {form.tags?.length ? (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {form.tags.map((t) => (
                <span key={t} className="px-2 py-0.5 rounded-md border border-[#2c3122] text-[10.5px] font-mono text-[#a9aa9a]">{t}</span>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </Section>
  );
}

/* ============================== Bits ============================== */
function Section({ letter, title, children }) {
  return (
    <section className="pb-6 border-b border-[#22261b] last:border-b-0 last:pb-0 space-y-3">
      <div className="flex items-center gap-2.5">
        <span className="w-[22px] h-[22px] rounded-md grid place-items-center text-[12px] font-bold" style={{ background: "#c9a86b", color: "#1a1407" }}>{letter}</span>
        <h3 className="text-[12.5px] tracking-[.16em] uppercase text-[#a9aa9a] m-0">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] tracking-[.16em] uppercase text-[#6e6f5f] mb-1.5">{label}</span>
      {children}
      {hint && (
        <span className="block text-[10.5px] text-[#7a7c66] mt-1.5 leading-[1.5] normal-case">
          {hint}
        </span>
      )}
    </label>
  );
}

/* =========================================================================
   TIER EDITOR — the structured pricing table that powers the public
   Mix-&-Match Drop Builder. Operator edits rows here, public site renders
   the big weight pills on the product modal.
   ========================================================================= */
const DEFAULT_TIER_PRESET_BREAKDOWN = [
  { key: "3.5",  label: "3.5g",      price: 25,  in_stock: true, note: "" },
  { key: "7",    label: "7g",        price: 40,  in_stock: true, note: "" },
  { key: "14",   label: "14g",       price: 75,  in_stock: true, note: "" },
  { key: "ZIP",  label: "ZIP · 28g", price: 110, in_stock: true, note: "" },
  { key: "QP",   label: "QP",        price: 400, in_stock: true, note: "" },
  { key: "HP",   label: "HP",        price: 725, in_stock: true, note: "" },
  { key: "DROP", label: "Full Drop", price: 0,   in_stock: true, note: "Contact for pricing" },
];

const DEFAULT_TIER_PRESET_WHOLESALE = [
  { key: "P", label: "P · Pound", price: 0, in_stock: true, note: "Set the per-pound price" },
];

function TierEditor({ form, set }) {
  const tiers = form.tiers || [];
  const isWholesale = form.category === "wholesale";
  const preset = isWholesale ? DEFAULT_TIER_PRESET_WHOLESALE : DEFAULT_TIER_PRESET_BREAKDOWN;
  const update = (i, patch) => {
    const next = tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t));
    set({ tiers: next });
  };
  const remove = (i) => set({ tiers: tiers.filter((_, idx) => idx !== i) });
  const add = () => set({ tiers: [...tiers, { key: "", label: "", price: 0, in_stock: true, note: "" }] });
  const applyPreset = () => set({ tiers: preset.map((t) => ({ ...t })) });
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= tiers.length) return;
    const next = [...tiers];
    [next[i], next[j]] = [next[j], next[i]];
    set({ tiers: next });
  };

  return (
    <div className="rounded-lg border border-[#c9a86b]/25 bg-[#1a1810]/40 p-3.5 space-y-3" data-testid="tier-editor">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10.5px] tracking-[.18em] uppercase text-[#c9a86b] font-mono">Mix &amp; Match Tiers</div>
          <div className="text-[10.5px] text-[#6e6f5f] mt-0.5 leading-[1.55]">
            {isWholesale ? (
              <>Pricing tiers for wholesale (pound-and-up). Each row becomes a "weight pill" + "Add to Drop" on the product modal. Most wholesale products use a single <code>P · Pound</code> tier — add bundles like <code>4P</code> or <code>HP</code> if you offer them.</>
            ) : (
              <>Structured pricing for the public Drop Builder. Each row becomes a big "weight pill" on the product modal. Use insider keys (<code>3.5 · 7 · 14 · ZIP · QP · HP · DROP</code>) — they map to the pill labels.</>
            )}
          </div>
        </div>
        {tiers.length === 0 && (
          <button
            type="button"
            data-testid="tier-preset-btn"
            onClick={applyPreset}
            className="shrink-0 px-3 py-2 text-[10.5px] tracking-[.16em] uppercase font-mono rounded-md border border-[#c9a86b]/40 text-[#c9a86b] hover:bg-[#c9a86b]/10 transition"
          >Use defaults</button>
        )}
      </div>

      {tiers.length === 0 ? (
        <div className="text-center py-6 text-[12px] text-[#6e6f5f]">
          No tiers yet. Click "Use defaults" above for the standard breakdown ladder, or "+ Add tier" to start fresh.
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-[#2c3122]">
          <div className="grid grid-cols-[44px_88px_1fr_100px_56px_72px] gap-1 px-2.5 py-2 bg-[#0f120c] text-[9.5px] tracking-[.2em] uppercase text-[#6e6f5f] font-mono">
            <span>#</span>
            <span>Key</span>
            <span>Label / note</span>
            <span>Price</span>
            <span>Stock</span>
            <span></span>
          </div>
          <ul>
            {tiers.map((t, i) => (
              <li key={i} className="grid grid-cols-[44px_88px_1fr_100px_56px_72px] gap-1 px-2.5 py-2 items-center border-t border-[#2c3122] bg-[#13160e]/60" data-testid={`tier-row-${i}`}>
                <div className="flex flex-col gap-0.5">
                  <button type="button" className="text-[#6e6f5f] hover:text-[#c9a86b] text-[10px]" onClick={() => move(i, -1)} aria-label="Move up" data-testid={`tier-up-${i}`}>▲</button>
                  <button type="button" className="text-[#6e6f5f] hover:text-[#c9a86b] text-[10px]" onClick={() => move(i, +1)} aria-label="Move down" data-testid={`tier-down-${i}`}>▼</button>
                </div>
                <input
                  type="text"
                  value={t.key}
                  onChange={(e) => update(i, { key: e.target.value.toUpperCase().replace(/\s+/g, "") })}
                  placeholder="3.5"
                  data-testid={`tier-key-${i}`}
                  className="bg-[#0a0c07] border border-[#2c3122] rounded px-2 py-1.5 text-[12px] font-mono uppercase text-[#c9a86b] focus:border-[#c9a86b] outline-none"
                />
                <input
                  type="text"
                  value={t.label}
                  onChange={(e) => update(i, { label: e.target.value })}
                  placeholder='Display label (e.g. "3.5g") · or short note'
                  data-testid={`tier-label-${i}`}
                  className="bg-[#0a0c07] border border-[#2c3122] rounded px-2 py-1.5 text-[12px] font-mono text-[#c9c9a9] focus:border-[#c9a86b] outline-none"
                />
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={Number.isFinite(t.price) ? t.price : 0}
                  onChange={(e) => update(i, { price: Number(e.target.value) || 0 })}
                  placeholder="0"
                  data-testid={`tier-price-${i}`}
                  className="bg-[#0a0c07] border border-[#2c3122] rounded px-2 py-1.5 text-[12px] font-mono text-[#c9c9a9] focus:border-[#c9a86b] outline-none"
                />
                <ToggleRow
                  data-testid={`tier-stock-${i}`}
                  label=""
                  checked={t.in_stock !== false}
                  onChange={(v) => update(i, { in_stock: v })}
                />
                <button
                  type="button"
                  data-testid={`tier-rm-${i}`}
                  onClick={() => remove(i)}
                  className="text-[9.5px] tracking-[.18em] uppercase font-mono text-[#7a4040] hover:text-[#ff8a8a] transition"
                >Remove</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={add}
        data-testid="tier-add-btn"
        className="w-full px-3 py-2 border border-dashed border-[#c9a86b]/30 rounded-md text-[10.5px] tracking-[.18em] uppercase font-mono text-[#c9a86b]/70 hover:text-[#c9a86b] hover:border-[#c9a86b]/60 transition"
      >+ Add Tier</button>
    </div>
  );
}

/* =========================================================================
   TAG LIST EDITOR — chip-style input for flavor/effect/best-for arrays.
   ========================================================================= */
function TagListEditor({ label, hint, value, onChange, testid }) {
  const [draft, setDraft] = React.useState("");
  const list = Array.isArray(value) ? value : [];
  const add = (raw) => {
    const items = String(raw || "")
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!items.length) return;
    onChange([...list, ...items.filter((it) => !list.includes(it))]);
    setDraft("");
  };
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));

  return (
    <div data-testid={testid}>
      <Field label={label} hint={hint}>
        <div className="rounded-md bg-[#0a0c07] border border-[#2c3122] focus-within:border-[#c9a86b] transition">
          {list.length > 0 && (
            <div className="flex flex-wrap gap-1.5 p-2 pb-1">
              {list.map((t, i) => (
                <span key={i} data-testid={`${testid}-chip-${i}`} className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full border border-[#c9a86b]/30 bg-[#c9a86b]/10 text-[11px] font-mono uppercase tracking-[.14em] text-[#c9a86b]">
                  {t}
                  <button type="button" onClick={() => remove(i)} aria-label={`Remove ${t}`} className="w-4 h-4 rounded-full hover:bg-[#c9a86b]/20 text-[10px] leading-none">×</button>
                </span>
              ))}
            </div>
          )}
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(draft); }
              else if (e.key === "Backspace" && !draft && list.length) remove(list.length - 1);
            }}
            onBlur={() => draft && add(draft)}
            placeholder={list.length ? "Add another…" : "Type and press Enter…"}
            className="w-full bg-transparent px-3 py-2 text-[12.5px] font-mono text-[#c9c9a9] outline-none"
          />
        </div>
      </Field>
    </div>
  );
}

function ToggleRow({ label, checked, onChange, "data-testid": testid, ...rest }) {
  return (
    <label data-testid={testid} className="flex items-center gap-2 cursor-pointer">
      <span className={`relative w-9 h-5 rounded-full border transition ${checked ? "bg-[#2d3a23] border-[#5fa15a]" : "bg-[#1f2218] border-[#2c3122]"}`}>
        <span className={`absolute top-[1px] w-3.5 h-3.5 rounded-full transition-all ${checked ? "left-[18px] bg-[#5fa15a]" : "left-[2px] bg-[#6e6f5f]"}`} />
      </span>
      <input type="checkbox" {...rest} checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="hidden" />
      <span className="text-[12px] text-[#a9aa9a]">{label}</span>
    </label>
  );
}

/* ---- helpers ---- */
function ThumbImage({ src, alt }) {
  const [fit, setFit] = React.useState("cover");
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onLoad={(e) => {
        const r = e.currentTarget.naturalWidth / e.currentTarget.naturalHeight;
        setFit(r >= 1.05 ? "cover" : "contain");
      }}
      className="absolute inset-0 w-full h-full"
      style={{ objectFit: fit, objectPosition: "center" }}
    />
  );
}

function stockOf(av) {
  const s = (av || "").toLowerCase();
  if (s.includes("sold")) return { cls: "text-[#c97e6a]", color: "#d97757" };
  if (s.includes("limit")) return { cls: "text-[#e2c46d]", color: "#e2c46d" };
  if (s.includes("coming")) return { cls: "text-[#9aa0c4]", color: "#9aa0c4" };
  return { cls: "text-[#a8d39d]", color: "#5fa15a" };
}

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  return `${d} d ago`;
}
function latestUpdate(list) {
  return list.map((p) => p.updated_at || p.created_at).filter(Boolean).sort().pop();
}
function latestUpdateName(list) {
  const sorted = [...list].sort((a, b) => (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || ""));
  return sorted[0]?.name || "";
}
